import React, { useEffect, useState, useCallback, useRef } from 'react';
import { ChevronLeft, ChevronRight, X as XIcon, Edit3, Save, Loader2, Award } from 'lucide-react';
import { Button } from '../ui/button';
import { getProposalEntry, slidesForProposal } from './proposalRegistry';
import BrokerProposalEditor from './BrokerProposalEditor';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * ProposalPresentation — broker-side "present mode" panel.
 *
 * Lives alongside MeetingRoom (broker side only). Shows the slide deck the
 * broker is about to walk through. As the broker advances slides or edits
 * values, the changes broadcast over the existing signaling app-channel so
 * the guest's PublicProposalViewer mirrors instantly.
 *
 * Persistence:
 *   - Initial proposal payload loaded from /api/proposals/{id}
 *   - Edits autosaved every ~1.5s (debounced)
 *   - Slide changes are NOT persisted (ephemeral broker pointer)
 *
 * Sync app-message protocol (over signaling.send('app', payload)):
 *   {type:'slide:goto', slide:int}
 *   {type:'value:update', payload}      // full payload snapshot — simple & safe
 *   {type:'accept'}                     // broker confirms acceptance
 */
const ProposalPresentation = ({
  proposalId,
  authToken,
  signalingSend, // (kind, payload) => void from MeetingRoom's signaling client
  clientViewing, // {slideIdx, since} | null — broker sees which slide guest is on
  onClose,
}) => {
  const [proposal, setProposal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [slideIdx, setSlideIdx] = useState(0);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const saveTimerRef = useRef(null);
  const lastSentPayloadRef = useRef(null);

  // ── Load proposal ──
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${BACKEND}/api/proposals/${proposalId}`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        if (!res.ok) throw new Error('Failed to load proposal');
        const data = await res.json();
        if (cancelled) return;
        setProposal(data);
        setAccepted(data.status === 'accepted');
      } catch (e) {
        // surface in UI later — for now silent
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [proposalId, authToken]);

  // ── Helpers: change slide / payload + broadcast ──
  const entry = getProposalEntry(proposal?.type);
  const SLIDES = slidesForProposal(proposal?.type, proposal?.payload);
  const renderSlideFn = entry.render;

  const goSlide = useCallback((target) => {
    const max = SLIDES.length - 1;
    const idx = Math.max(0, Math.min(max, target));
    setSlideIdx(idx);
    if (signalingSend) signalingSend('app', { type: 'slide:goto', slide: idx });
  }, [signalingSend, SLIDES.length]);

  const updatePayload = useCallback((patch) => {
    setProposal((prev) => {
      if (!prev) return prev;
      const newPayload = typeof patch === 'function' ? patch(prev.payload || {}) : { ...(prev.payload || {}), ...patch };
      const next = { ...prev, payload: newPayload };
      // Broadcast snapshot
      if (signalingSend) signalingSend('app', { type: 'value:update', proposalType: prev.type, payload: newPayload });
      // Debounced autosave
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(async () => {
        setSaving(true);
        try {
          await fetch(`${BACKEND}/api/proposals/${proposalId}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
            body: JSON.stringify({ payload: newPayload }),
          });
        } finally {
          setSaving(false);
        }
      }, 1500);
      return next;
    });
  }, [proposalId, authToken, signalingSend]);

  // Re-broadcast current state when sync helper becomes available (broker
  // entered presentation mode after guest already connected).
  useEffect(() => {
    if (!signalingSend || !proposal) return;
    const snap = JSON.stringify(proposal.payload);
    if (snap !== lastSentPayloadRef.current) {
      lastSentPayloadRef.current = snap;
      signalingSend('app', { type: 'value:update', proposalType: proposal.type, payload: proposal.payload });
      signalingSend('app', { type: 'slide:goto', slide: slideIdx });
    }
  }, [signalingSend, proposal, slideIdx]);

  const onAccept = async () => {
    setAccepted(true);
    try {
      await fetch(`${BACKEND}/api/proposals/${proposalId}/accept`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${authToken}` },
      });
    } catch (e) { /* */ }
    if (signalingSend) signalingSend('app', { type: 'accept' });
  };

  if (loading) {
    return (
      <div className="absolute inset-0 z-30 bg-slate-900/95 flex items-center justify-center text-white">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
      </div>
    );
  }
  if (!proposal) return null;

  return (
    <div className="absolute inset-0 z-30 bg-slate-100 flex flex-col" data-testid="proposal-presentation">
      {/* Top toolbar */}
      <div className="bg-slate-950/85 backdrop-blur text-white px-3 sm:px-5 py-2 flex items-center justify-between gap-3 z-10 shrink-0">
        <div className="flex items-center gap-2 text-xs">
          <span className="font-semibold uppercase tracking-wide text-emerald-400">Presenting</span>
          <span className="text-slate-300 hidden sm:inline">·</span>
          <span className="text-slate-300 hidden sm:inline truncate max-w-[180px]">
            {proposal.title || `${entry.label} — ${proposal.client_name || 'client'}`}
          </span>
          {saving && <span className="text-slate-400 hidden sm:inline italic">· saving…</span>}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => setEditing(!editing)} className="text-white hover:bg-slate-800" data-testid="toggle-edit-mode">
            <Edit3 className="h-4 w-4 mr-1" />
            <span className="hidden sm:inline">{editing ? 'Hide editor' : 'Edit values'}</span>
          </Button>
          <Button variant="ghost" size="sm" onClick={onClose} className="text-white hover:bg-slate-800" data-testid="close-presentation">
            <XIcon className="h-4 w-4 mr-1" />
            <span className="hidden sm:inline">Stop</span>
          </Button>
        </div>
      </div>

      {/* Slide canvas */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        {renderSlideFn({
          index: slideIdx,
          payload: proposal.payload,
          clientName: proposal.client_name,
          brokerName: proposal.broker_name,
          readonly: false,
          accepted,
          onAccept,
        })}
      </div>

      {/* Bottom slide nav */}
      <div className="bg-slate-950/90 backdrop-blur text-white px-3 sm:px-5 py-3 flex items-center justify-between gap-3 z-10 shrink-0">
        <Button onClick={() => goSlide(slideIdx - 1)} disabled={slideIdx === 0} variant="outline" size="sm" className="bg-transparent border-white/20 hover:bg-white/10" data-testid="slide-prev">
          <ChevronLeft className="h-4 w-4 mr-1" /> <span className="hidden sm:inline">Back</span>
        </Button>
        <div className="flex items-center gap-1.5 overflow-x-auto px-2">
          {SLIDES.map((s, i) => {
            const isClientHere = clientViewing && clientViewing.slideIdx === i;
            return (
              <button
                key={s.key}
                onClick={() => goSlide(i)}
                data-testid={`slide-dot-${i}`}
                className={`relative min-w-[28px] h-1.5 rounded-full transition-all ${
                  i === slideIdx ? 'bg-emerald-400 w-8' : 'bg-white/20 hover:bg-white/40'
                }`}
                title={s.title + (isClientHere ? ' — client is here' : '')}
              >
                {isClientHere && (
                  <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 flex h-2 w-2">
                    <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                  </span>
                )}
              </button>
            );
          })}
          <span className="text-xs text-slate-400 ml-3 whitespace-nowrap">{slideIdx + 1} / {SLIDES.length}</span>
        </div>
        <Button onClick={() => goSlide(slideIdx + 1)} disabled={slideIdx === SLIDES.length - 1} variant="outline" size="sm" className="bg-transparent border-white/20 hover:bg-white/10" data-testid="slide-next">
          <span className="hidden sm:inline">Next</span> <ChevronRight className="h-4 w-4 ml-1" />
        </Button>
      </div>

      {/* Side editor drawer — payload-shape varies by proposal.type */}
      {editing && proposal.type === 'consolidation' && (
        <ProposalEditor
          payload={proposal.payload || {}}
          onChange={(patch) => updatePayload(patch)}
          onClose={() => setEditing(false)}
        />
      )}
      {editing && (proposal.type === 'purchase' || proposal.type === 'refinance') && (
        <BrokerProposalEditor
          payload={proposal.payload || {}}
          onChange={(patch) => updatePayload(patch)}
          onClose={() => setEditing(false)}
        />
      )}
    </div>
  );
};

// ─────────────────── Live editor side panel ───────────────────
const ProposalEditor = ({ payload, onChange, onClose }) => {
  const debts = payload.debts || [];
  const mortgage = payload.mortgage || { remainingBalance: 0, interestRate: 0, remainingTerm: 0 };
  const consolidationRate = payload.consolidationRate ?? '';

  const setMortgage = (patch) => onChange((p) => ({ ...p, mortgage: { ...(p.mortgage || {}), ...patch } }));
  const setRate = (v) => onChange({ consolidationRate: v });
  const setDebt = (i, patch) => onChange((p) => {
    const arr = [...(p.debts || [])];
    arr[i] = { ...arr[i], ...patch };
    return { ...p, debts: arr };
  });
  const addDebt = () => onChange((p) => ({ ...p, debts: [...(p.debts || []), { name: '', amount: 0, rate: 0, term: 5 }] }));
  const removeDebt = (i) => onChange((p) => {
    const arr = [...(p.debts || [])];
    arr.splice(i, 1);
    return { ...p, debts: arr };
  });

  return (
    <div className="absolute right-0 top-12 bottom-16 w-full sm:w-80 bg-white shadow-2xl border-l border-slate-200 overflow-y-auto z-20" data-testid="proposal-editor">
      <div className="sticky top-0 bg-slate-100 border-b px-4 py-2 flex items-center justify-between">
        <div className="text-xs uppercase tracking-wide font-bold text-slate-600">Live Editor</div>
        <button onClick={onClose} className="text-slate-500 hover:text-slate-700"><XIcon className="h-4 w-4" /></button>
      </div>
      <div className="p-4 space-y-5 text-sm">
        <div>
          <p className="font-bold text-slate-700 mb-2">Mortgage</p>
          <Field label="Remaining balance" type="number" value={mortgage.remainingBalance ?? ''} onChange={(v) => setMortgage({ remainingBalance: Number(v) || 0 })} />
          <Field label="Current rate (%)" type="number" step="0.01" value={mortgage.interestRate ?? ''} onChange={(v) => setMortgage({ interestRate: Number(v) || 0 })} />
          <Field label="Remaining term (years)" type="number" value={mortgage.remainingTerm ?? ''} onChange={(v) => setMortgage({ remainingTerm: Number(v) || 0 })} />
        </div>
        <div>
          <p className="font-bold text-slate-700 mb-2">Consolidation Rate (%)</p>
          <Field type="number" step="0.01" value={consolidationRate} onChange={(v) => setRate(Number(v) || 0)} />
        </div>
        <div>
          <div className="flex items-center justify-between mb-2">
            <p className="font-bold text-slate-700">Debts</p>
            <button onClick={addDebt} className="text-xs text-emerald-600 font-semibold hover:underline" data-testid="add-debt-btn">+ Add</button>
          </div>
          {debts.length === 0 && <p className="text-xs text-slate-400 italic">No debts yet</p>}
          {debts.map((d, i) => (
            <div key={i} className="border border-slate-200 rounded p-2 mb-2 space-y-2">
              <div className="flex items-center gap-2">
                <input
                  className="flex-1 border rounded px-2 py-1 text-xs"
                  placeholder="e.g. Personal loan"
                  value={d.name || ''}
                  onChange={(e) => setDebt(i, { name: e.target.value })}
                />
                <button onClick={() => removeDebt(i)} className="text-rose-500 text-xs font-semibold hover:underline">×</button>
              </div>
              <div className="grid grid-cols-3 gap-1">
                <input type="number" className="border rounded px-2 py-1 text-xs" placeholder="Amount" value={d.amount || ''} onChange={(e) => setDebt(i, { amount: Number(e.target.value) || 0 })} />
                <input type="number" step="0.01" className="border rounded px-2 py-1 text-xs" placeholder="Rate %" value={d.rate || ''} onChange={(e) => setDebt(i, { rate: Number(e.target.value) || 0 })} />
                <input type="number" className="border rounded px-2 py-1 text-xs" placeholder="Term yrs" value={d.term || ''} onChange={(e) => setDebt(i, { term: Number(e.target.value) || 0 })} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, value, onChange, type = 'text', step }) => (
  <div className="mb-2">
    {label && <label className="text-[11px] uppercase tracking-wide font-semibold text-slate-500 block mb-1">{label}</label>}
    <input
      type={type}
      step={step}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full border border-slate-200 rounded px-2 py-1.5 text-sm focus:outline-none focus:border-emerald-500"
    />
  </div>
);

export default ProposalPresentation;
